import { useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Header } from '@app/sections/header';
import { Strings } from '@app/constants';
import { selectRoleState } from '@app/modules/role';
import { selectMyProfileFormState } from '@app/modules/my-profile';
import { useTable } from '@app/components/table';
import { useDialog, useRequestState, useSearch } from '@app/hooks';
import { AddEmployeePage } from './add-employee/add-employee-page';
import { EmployeeList } from './employee-list';
import { getEmployees } from '../slice';
import { selectEmployeesState } from '../selectors';
import { useAuthRole } from '@app/utils';

const EmployeeListPage = () => {
  const tableProps = useTable();
  const addEmployeeDialog = useDialog();
  const dispatch = useDispatch();
  const { isManager } = useAuthRole();

  useEffect(() => {
    dispatch(getEmployees());
  }, [dispatch]);

  const { data, loading } = useRequestState({
    stateSelector: selectEmployeesState,
    errorShown: true,
  });

  const { data: profileData } = useRequestState({
    stateSelector: selectMyProfileFormState,
    errorShown: true,
  });
  const profileId = profileData?.id;

  const roleState = useSelector(selectRoleState);
  const roles = roleState?.data?.records;

  const enrichedRecords = useMemo(() => {
    if (!data?.records || !roles || profileId === undefined) return [];

    if (profileId === 1) {
      return data.records
        .filter((record) => record.roleId !== 1)
        .map((record) => {
          const matchedRole = roles.find((role) => role?.id === record?.roleId);
          return {
            ...record,
            roleName: matchedRole?.name ?? '',
          };
        });
    }

    return data.records
      .filter((record) => record.roleId !== 1)
      .filter((record) => (record.managerId as string[])?.includes(String(profileId)))
      .map((record) => {
        const matchedRole = roles.find((role) => role?.id === record?.roleId);
        return {
          ...record,
          roleName: matchedRole?.name ?? '',
        };
      });
  }, [data?.records, roles, profileId]);

  const searchProps = useSearch({
    data: enrichedRecords ?? [],
    fields: ['firstName', 'lastName', 'email', 'phone', 'id', 'status', 'roleName'],
  });

  return (
    <>
      <Header
        title={isManager ? Strings.page_title.myTeamMembers : Strings.page_title.employees}
        helperText={''}
        actionButtonTitle={isManager ? '' : Strings.button.addEmployee}
        onClickActionButton={addEmployeeDialog.show}
        searchBarProps={{
          placeholder: Strings.common.searchNameEmailPhoneRole,
          onChange: searchProps.handleSearchChange,
        }}
      />

      <EmployeeList loading={loading} tableProps={tableProps} rows={searchProps.filteredRows} />

      {addEmployeeDialog.visible && (
        <AddEmployeePage open={addEmployeeDialog.visible} onClose={addEmployeeDialog.hide} />
      )}
    </>
  );
};

export { EmployeeListPage };
